// Cold email: a read-only mirror of Instantly (docs/specs/instantly.md).
// Instantly sends; this screen shows what is going out and what comes back.

import { useColdEmail, type ColdReplyRow } from '../../lib/data';
import { fDay, fShort, num, pct } from '../../lib/format';
import { ErrorBox } from '../../ui/kit';

const CATEGORY: Record<string, [string, string]> = {
  interested: ['Interested', 'te'],
  not_now: ['Not now', 'or'],
  other_contact: ['Other contact', ''],
  not_interested: ['Not interested', 'red'],
  out_of_office: ['Out of office', 'gr'],
  unsubscribe: ['Unsubscribe', 'red'],
  auto_reply: ['Auto reply', 'gr'],
  unknown: ['New', 'gr'],
};

const STATUS_PILL: Record<string, string> = {
  Active: 'te',
  Paused: 'gr',
  Draft: 'gr',
  Completed: '',
};

export function ColdEmail() {
  const q = useColdEmail();
  const d = q.data;
  const active = (d?.campaigns ?? []).filter((c) => c.status === 'Active');
  const today = active.reduce((a, c) => a + c.sent_today, 0);
  const cap = active.reduce((a, c) => a + c.daily_cap, 0);
  const tot = (d?.campaigns ?? []).reduce(
    (a, c) => ({
      sent: a.sent + c.sent_total,
      replies: a.replies + c.replies,
      positive: a.positive + c.positive,
      bounced: a.bounced + c.bounced,
    }),
    { sent: 0, replies: 0, positive: 0, bounced: 0 },
  );

  // One bar per day of the last 14, summed over campaigns.
  const days = Array.from({ length: 14 }, (_, i) => {
    const date = new Date(Date.now() - (13 - i) * 86_400_000);
    const key = date.toISOString().slice(0, 10);
    const sent = (d?.daily ?? []).filter((r) => r.day === key).reduce((a, r) => a + r.sent, 0);
    return { key, sent, label: date.toLocaleDateString('en-GB', { weekday: 'short' })[0]! };
  });
  const max = Math.max(1, ...days.map((x) => x.sent));
  const names = new Map((d?.campaigns ?? []).map((c) => [c.id, c.name]));

  return (
    <>
      <div className="head">
        <div>
          <h1>
            Cold <span className="grad">email</span>
          </h1>
          <p>Sent through Instantly. Everything you need to know, in one place.</p>
        </div>
        <div className="head-r">
          {d?.campaigns[0]?.synced_at && (
            <span className="pill te">
              <span className="sdot ok" aria-hidden /> Instantly synced{' '}
              {fShort(d.campaigns[0].synced_at)}
            </span>
          )}
        </div>
      </div>
      <ErrorBox error={q.error} />
      {!d ? (
        <section className="card sec loading">Loading…</section>
      ) : d.campaigns.length === 0 ? (
        <section className="card sec" data-testid="view-coldemail">
          <p className="text-muted">
            No campaigns yet. They appear within 10 minutes of Instantly being connected.
          </p>
        </section>
      ) : (
        <>
          <div className="kpis" data-testid="view-coldemail" aria-label="Cold email numbers">
            <div className="card kpi">
              <div className="k">Sent today</div>
              <div className="v">
                {today} <small>/ {cap}</small>
              </div>
              <div className="meter">
                <i style={{ width: `${Math.min(100, (today / Math.max(1, cap)) * 100)}%` }} />
              </div>
              <div className="s">Across {active.length} active campaigns</div>
            </div>
            <div className="card kpi">
              <div className="k">Reply rate</div>
              <div className="v">{pct(tot.replies, tot.sent)}</div>
              <div className="s">
                {tot.replies} replies from {num(tot.sent)} emails
              </div>
            </div>
            <div className="card kpi">
              <div className="k">Positive replies</div>
              <div className="v">{pct(tot.positive, tot.sent)}</div>
              <div className="s">{tot.positive} interested</div>
            </div>
            <div className="card kpi">
              <div className="k">Bounced</div>
              <div className="v">{pct(tot.bounced, tot.sent)}</div>
              <div className="s">{tot.bounced} emails, keep it under 2%</div>
            </div>
          </div>

          <div className="two">
            <section className="card">
              <div className="chead">
                <span className="lbl g">Emails per day</span>
              </div>
              <p className="sub">Last 14 days.</p>
              <div className="bars">
                {days.map((x) => (
                  <div className="c" key={x.key}>
                    <em>{x.sent || ''}</em>
                    <div className="b" style={{ height: `${(x.sent / max) * 100}%` }} />
                    <span>{x.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ height: 18 }} />
            </section>
            <section className="card" data-testid="ce-inboxes">
              <div className="chead">
                <span className="lbl p">Sending inboxes</span>
              </div>
              <div className="list-pad">
                {d.inboxes.length === 0 && <div className="empty">No inboxes yet.</div>}
                {d.inboxes.map((x) => (
                  <div className="trow" style={{ gridTemplateColumns: 'auto 1fr auto' }} key={x.id}>
                    <span
                      className={`sdot ${x.status === 'error' ? 'err' : x.status}`}
                      aria-hidden
                    />
                    <div>
                      <div className="t" style={{ fontSize: 15 }}>
                        {x.email}
                      </div>
                      <div className="m">
                        {x.daily_limit} a day
                        {x.warmup_score != null && x.warmup_score < 100
                          ? `, warming up ${x.warmup_score}%`
                          : ''}
                      </div>
                    </div>
                    {x.status === 'error' ? (
                      <span className="pill sm red">{x.note ?? 'Problem'}</span>
                    ) : x.status === 'warn' ? (
                      <span className="pill sm or">{x.note ?? 'Check'}</span>
                    ) : (
                      <span className="pill sm te">Healthy</span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="card sec" data-testid="ce-campaigns">
            <div className="chead">
              <span className="lbl g">Campaigns</span>
            </div>
            <div className="scroll" tabIndex={0} role="region" aria-label="Campaigns table">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th className="num">Today</th>
                    <th className="num">Contacts</th>
                    <th className="num">Sent</th>
                    <th className="num">Reply</th>
                    <th className="num">Positive</th>
                    <th className="num">Bounced</th>
                  </tr>
                </thead>
                <tbody>
                  {d.campaigns.map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.market && <span className="mk">{c.market}</span>}{' '}
                        <b style={{ marginLeft: c.market ? 6 : 0 }}>{c.name}</b>
                      </td>
                      <td>
                        <span className={`pill sm ${STATUS_PILL[c.status] ?? 'gr'}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="num">
                        {c.status === 'Active' ? `${c.sent_today}/${c.daily_cap}` : '–'}
                      </td>
                      <td className="num">{num(c.leads_count)}</td>
                      <td className="num">{num(c.sent_total)}</td>
                      <td className="num">{pct(c.replies, c.sent_total)}</td>
                      <td className="num">
                        <b>{pct(c.positive, c.sent_total)}</b>
                      </td>
                      <td className="num">{pct(c.bounced, c.sent_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ height: 12 }} />
          </section>

          <section className="card sec" data-testid="ce-replies">
            <div className="chead">
              <span className="lbl p">Replies</span>
            </div>
            <div className="list-pad">
              {d.replies.length === 0 && <div className="empty">No replies yet.</div>}
              {d.replies.map((r) => (
                <Reply
                  key={r.id}
                  r={r}
                  campaign={r.campaign_id ? names.get(r.campaign_id) : undefined}
                />
              ))}
            </div>
          </section>
        </>
      )}
    </>
  );
}

function Reply({ r, campaign }: { r: ColdReplyRow; campaign: string | undefined }) {
  const [label, cls] = CATEGORY[r.category] ?? CATEGORY.unknown!;
  return (
    <div className="trow" style={{ gridTemplateColumns: '1fr auto' }}>
      <div>
        <div className="t" style={{ fontSize: 16 }}>
          {r.from_name}{' '}
          <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 14 }}>
            {r.from_email}
          </span>
        </div>
        <div className="m">{r.body}</div>
        {campaign && (
          <div className="m" style={{ fontSize: 13 }}>
            {campaign}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right' }}>
        <span className={`pill sm ${cls}`}>{label}</span>
        <div className="m" style={{ fontSize: 13, marginTop: 4 }}>
          {fDay(r.received_at)}
        </div>
      </div>
    </div>
  );
}
